/**
 * GLAMRDiP tiered free-gift reconciler — Joel 2026-07-18 (birthday sale).
 *
 * Reads window.__gdTierGifts (from sections/tier-gifts.liquid). Up to 4 dollar-threshold tiers
 * (incl. an optional ~0 "Tier-0" Mystery-Gift floor so every order with real non-kit spend
 * carries a gift), HIGHEST EARNED WINS. Pure desiredGifts(cart) → Map(variant→qty); on every cart event the
 * cart is DIFFED against desired and converged (no incremental add/remove logic). Gift lines
 * carry property _gd_gift:'tier-N' (+ _no_edit, _gd_gift_caption). Stateless → two tabs converge.
 *
 * Coexistence: independent of gd-gift-with-purchase.js (property _free_gift, product-triggered)
 * and gd-cart-sanitizer.js. Serialized with both via the shared window.__gdCartProcessing flag;
 * this engine only ever touches _gd_gift lines. The sanitizer + cart drawer + cart page are
 * extended elsewhere to recognise _gd_gift (keep it, don't qty-force it, render it as a gift).
 *
 * Triggers: page load · cart:updated / product-added-to-cart / gd:cart-refreshed · fetch
 * monkey-patch on /cart/(add|change|update).js · CHECKOUT CLICK (intercept → reconcile → await
 * stable → proceed to /checkout).
 *
 * Hardening (adversarial review 2026-07-18, pre-sale):
 *   - res.ok is checked on every mutation; `changed` is set ONLY on a real success. A persistently
 *     failing add/remove (gift OOS mid-session, 429 throttling, 5xx) no longer self-loops — after
 *     MAX_NOPROGRESS failed passes the engine backs off for BACKOFF_MS instead of hammering the
 *     cart API (which under load would amplify a throttle storm). [BLOCKER-1]
 *   - Every fetch has an AbortController timeout so one stalled request can't wedge the shared
 *     mutex and freeze all three engines. [MEDIUM-4]
 *   - Checkout click preventDefault()s FIRST, then the busy-guard — a rage-click during the
 *     reconcile can't fall through to the anchor's native navigation. [HIGH-2]
 *   - A tier gift whose tier tag / caption drifted (variant shared across tiers) is remove+re-added
 *     because /cart/change.js can't mutate line properties. [MEDIUM-8]
 *   - Variants already owned by the GWP _free_gift engine are excluded from tier sets so a product
 *     can't be gifted twice. [MEDIUM-6]
 */
(function () {
  'use strict';

  var CONFIG = window.__gdTierGifts || {};
  if (!CONFIG.enabled || !Array.isArray(CONFIG.tiers) || CONFIG.tiers.length === 0) return;

  // Enabled tiers with >=1 gift, sorted HIGHEST threshold first (highest earned wins).
  var TIERS = CONFIG.tiers
    .filter(function (t) { return t && Array.isArray(t.gifts) && t.gifts.length > 0 && Number(t.thresholdCents) >= 0; })
    .sort(function (a, b) { return Number(b.thresholdCents) - Number(a.thresholdCents); });

  // [MEDIUM-6] Defensive: never gift a variant the product-triggered GWP engine already owns as a
  // _free_gift reward — otherwise both reconcilers add it (different property namespaces) and the
  // product is given away twice. The section doc mandates disjoint variants; this enforces it.
  try {
    var gwpRewards = new Set();
    (window.__gdGiftRules || []).forEach(function (r) {
      if (r && Array.isArray(r.rewards)) r.rewards.forEach(function (v) { gwpRewards.add(Number(v)); });
    });
    if (gwpRewards.size) {
      TIERS.forEach(function (t) { t.gifts = t.gifts.filter(function (g) { return !gwpRewards.has(Number(g.variant)); }); });
    }
  } catch (e) { /* fail-open: keep configured gifts */ }

  TIERS = TIERS.filter(function (t) { return t.gifts.length > 0; });
  if (!TIERS.length) return;

  // [Joel 2026-07-18] Tier eligibility = NON-KIT spend ONLY. Kit/bundle value never earns tiers.
  // Two exclusion criteria applied in eligibleTotalCents(): (1) any line carrying __bundle_id
  // (bundle/kit parent + all its $0 components — covers Salon Kit + Instant Gels builder), and
  // (2) any line whose product is in this config-driven list (the kit-gift trigger products, e.g.
  // the Salon Kit / IG bundle parents) — belt-and-suspenders for a kit added outside the builder.
  var EXCLUDE_PRODUCTS = new Set((CONFIG.excludeProductIds || []).map(Number).filter(function (n) { return n; }));

  if (typeof window.__gdCartProcessing !== 'boolean') window.__gdCartProcessing = false;

  var GIFT_PROP = '_gd_gift';
  var CAPTION_PROP = '_gd_gift_caption';
  var pendingTimer = 0;

  // [BLOCKER-1] / [MEDIUM-4] anti-storm + timeout tunables
  var FETCH_TIMEOUT_MS = 6000;   // one stalled request can't wedge the shared mutex
  var MAX_NOPROGRESS = 3;        // consecutive failed-convergence passes before backing off
  var BACKOFF_MS = 20000;        // cool-down while the cart API is failing our mutations
  var noProgress = 0;
  var backoffUntil = 0;
  function nowMs() { try { return Date.now(); } catch (e) { return 0; } }

  // Presentment-safe threshold: thresholds are set in STORE currency; cart.total_price is in
  // the PRESENTMENT currency. presentment = store × rate. rate = 1.0 in a single-currency store,
  // so this degrades to a plain comparison.
  function currencyRate() {
    try {
      var n = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return (isFinite(n) && n > 0) ? n : 1;
    } catch (e) { return 1; }
  }

  // Eligibility subtotal (presentment cents) = cart total after discounts, excl. shipping AND excl.
  // all kit/bundle value. Sum non-excluded lines' final_line_price instead of cart.total_price so
  // bundle spend (parent + $0 components) and configured kit-trigger products never count toward a
  // tier. $0 gift lines contribute 0 anyway. [Joel 2026-07-18: non-kit spend only]
  function eligibleTotalCents(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var sum = 0;
    cart.items.forEach(function (it) {
      var p = it.properties || {};
      if (p.__bundle_id) return;                                 // bundle/kit line (parent + components)
      if (EXCLUDE_PRODUCTS.has(Number(it.product_id))) return;   // configured kit-gift trigger product
      var lp = Number(it.final_line_price);
      if (!isFinite(lp)) lp = (Number(it.final_price) || 0) * (Number(it.quantity) || 0);
      sum += lp;
    });
    return sum;
  }

  // PURE: cart → desired gift set Map(variantId→qty). Highest earned tier only. No side effects.
  function desiredGifts(cart) {
    var out = new Map();
    if (!cart || !Array.isArray(cart.items)) return out;
    var totalCents = eligibleTotalCents(cart);   // non-kit spend only (see eligibleTotalCents)
    // [Tier-0 floor guard — Joel 2026-07-18] A ~0-threshold floor tier (Mystery Gift) must NOT
    // gift an empty or kit-only cart. Require actual non-kit spend (>0) before ANY tier fires, so
    // "threshold ~0" means "any purchase", never $0. Kit-only orders still get the KIT gift via
    // the separate GWP engine; a truly empty cart gets nothing.
    if (totalCents <= 0) return out;
    var rate = currencyRate();
    for (var i = 0; i < TIERS.length; i++) {          // sorted highest-first
      var tier = TIERS[i];
      var thresholdPresentment = Math.round(Number(tier.thresholdCents) * rate);
      if (totalCents >= thresholdPresentment) {
        tier.gifts.forEach(function (g) {
          var vid = Number(g.variant);
          var qty = Math.max(1, Number(g.qty) || 1);
          if (vid) out.set(vid, Math.max(qty, out.get(vid) || 0));  // dup variant in a set → larger qty
        });
        out.__tier = tier.tier;
        out.__caption = tier.displayText || '';
        return out;   // no lower-tier merge — highest earned only
      }
    }
    return out;
  }

  function isTierGift(item) { return !!(item && item.properties && item.properties[GIFT_PROP]); }

  function debounced(delay) {
    // [Joel 2026-07-19] Once a checkout is in flight, do NOT start new event-driven reconciles.
    // The checkout owns the cart from click → navigate; a background event reconcile racing it is
    // exactly what stranded unearned gifts (two concurrent cart mutations → server-side lost update,
    // partial removal). The checkout's own reconcileUntilStable is the single writer during checkout.
    if (window.__gdCheckingOut) return;
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () { pendingTimer = 0; reconcile(false); }, delay || 150);
  }

  // [MEDIUM-4] every request is time-boxed; on timeout the AbortController rejects the promise,
  // the caller treats it as a failure, and the finally-block still releases the shared mutex.
  function timedFetch(url, opts) {
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var o = Object.assign({ credentials: 'same-origin' }, opts || {});
    if (controller) o.signal = controller.signal;
    var timer = controller ? setTimeout(function () { try { controller.abort(); } catch (e) {} }, FETCH_TIMEOUT_MS) : 0;
    return fetch(url, o).then(
      function (r) { if (timer) clearTimeout(timer); return r; },
      function (e) { if (timer) clearTimeout(timer); throw e; }
    );
  }

  function getCart() {
    return timedFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return (r && r.ok) ? r.json() : null; }).catch(function () { return null; });
  }
  function changeLine(key, quantity) {
    return timedFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }
  function addItems(items) {
    return timedFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    });
  }
  // Batch line-quantity update keyed by line-item KEY (returns the full cart, like change.js). Used
  // to zero MULTIPLE unearned gift lines in ONE round-trip on the checkout path (fast; more likely
  // to complete before the navigate-cap than sequential change.js calls).
  function updateItems(updatesByKey) {
    return timedFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updatesByKey })
    });
  }
  function warn(where, e) { try { console.warn('[gd-tier-gift] ' + where + ':', e && e.message); } catch (x) {} }
  function sleep(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }

  // Diff desired vs actual _gd_gift lines and converge. Returns true if it mutated the cart.
  // force=true bypasses the anti-storm backoff (used by the checkout intercept).
  async function reconcile(force) {
    if (!force && backoffUntil && nowMs() < backoffUntil) return false;      // [BLOCKER-1] cooling down
    if (window.__gdCartProcessing) { debounced(200); return false; }         // peer engine mid-flight — retry
    window.__gdCartProcessing = true;
    var changed = false;    // a real, ok-confirmed mutation happened
    var okAll = true;       // every attempted mutation returned ok
    var attempted = false;  // we had work to do this pass
    var addFired = false;
    try {
      var cart = await getCart();
      if (!cart || !Array.isArray(cart.items)) return false;   // read failure — not a convergence failure

      var desired = desiredGifts(cart);
      var tierTag = 'tier-' + (desired.__tier || 0);
      var caption = desired.__caption || '';
      function buildAdd(vid, qty) {
        var props = { _no_edit: 'true' };
        props[CAPTION_PROP] = caption;
        props[GIFT_PROP] = tierTag;
        return { id: vid, quantity: qty, properties: props };
      }

      var actual = new Map();   // variantId → {key, qty, tag, caption}  (only _gd_gift lines)
      cart.items.forEach(function (it) {
        if (isTierGift(it)) {
          var p = it.properties || {};
          actual.set(Number(it.variant_id), {
            key: it.key, qty: Number(it.quantity) || 0, tag: p[GIFT_PROP], caption: p[CAPTION_PROP] || ''
          });
        }
      });

      var toRemove = [], toAdjust = [], toAdd = [];
      actual.forEach(function (info, vid) {
        if (!desired.has(vid)) { toRemove.push(info.key); return; }
        var wantQty = desired.get(vid);
        // [MEDIUM-8] tier tag / caption can't be mutated via change.js → remove + re-add fresh.
        if (info.tag !== tierTag || info.caption !== caption) {
          toRemove.push(info.key);
          toAdd.push(buildAdd(vid, wantQty));
        } else if (info.qty !== wantQty) {
          toAdjust.push({ key: info.key, quantity: wantQty });
        }
      });
      desired.forEach(function (qty, vid) {
        if (!actual.has(vid)) toAdd.push(buildAdd(vid, qty));
      });

      attempted = (toRemove.length + toAdjust.length + toAdd.length) > 0;
      var freshCart = null;   // [A2.3] reuse the last mutation's returned cart JSON for the drawer

      // [A2.2 add-first-then-zero — Joel 2026-07-19] ADD the incoming gift(s) BEFORE removing the
      // outgoing ones, so a tier cross never flashes a gift-less moment in the drawer. Order is
      // add → adjust → remove. A brief $0 over-gift between the add and the remove is harmless.
      // (Same-variant tag-drift = a new line then old line removed; different lines, no conflict.)
      // /cart/add.js returns only the added items (no total_price/other lines) → never reused as
      // freshCart; /cart/change.js + /cart/update.js DO return the full cart, so freshCart is
      // captured from those. Mutation ORDER depends on the caller:
      if (force) {
        // CHECKOUT PATH — REMOVE-FIRST (Joel 2026-07-19). Zero EVERY unearned gift line in ONE fast
        // /cart/update.js call BEFORE attempting adds. If the 1500ms navigate-cap strands the
        // reconcile mid-flight, it strands a MISSING earned gift (Flow B nets it — customer-friendly)
        // rather than an UNEARNED gift (company cost). Removals batched = one round-trip = fast.
        if (toRemove.length) {
          var zero = {};
          for (var z = 0; z < toRemove.length; z++) zero[toRemove[z]] = 0;
          try { var ru = await updateItems(zero); if (ru && ru.ok) { changed = true; freshCart = await ru.json().catch(function () { return freshCart; }); } else okAll = false; }
          catch (e) { okAll = false; warn('remove-batch', e); }
        }
        if (toAdd.length) {
          addFired = true;
          try { var rd = await addItems(toAdd); if (rd && rd.ok) changed = true; else okAll = false; }
          catch (e) { okAll = false; warn('add', e); }
        }
        for (var a = 0; a < toAdjust.length; a++) {
          try { var ra = await changeLine(toAdjust[a].key, toAdjust[a].quantity); if (ra && ra.ok) { changed = true; freshCart = await ra.json().catch(function () { return freshCart; }); } else okAll = false; }
          catch (e) { okAll = false; warn('adjust', e); }
        }
      } else {
        // EVENT PATH — ADD-FIRST [A2.2]: a tier cross never flashes a gift-less moment in the drawer.
        if (toAdd.length) {
          addFired = true;
          try { var rd2 = await addItems(toAdd); if (rd2 && rd2.ok) changed = true; else okAll = false; }
          catch (e) { okAll = false; warn('add', e); }
        }
        for (var a2 = 0; a2 < toAdjust.length; a2++) {
          try { var ra2 = await changeLine(toAdjust[a2].key, toAdjust[a2].quantity); if (ra2 && ra2.ok) { changed = true; freshCart = await ra2.json().catch(function () { return freshCart; }); } else okAll = false; }
          catch (e) { okAll = false; warn('adjust', e); }
        }
        // Removes LAST (add-first, no flash) but BATCHED into one update.js call — so the whole
        // event reconcile finishes fast + releases the mutex promptly. A slow sequential remove
        // loop here is what let unearned gifts survive a drop→instant-checkout race (the event
        // reconcile still held the mutex, mid-remove, when the checkout cap navigated).
        if (toRemove.length) {
          var zero2 = {};
          for (var r2 = 0; r2 < toRemove.length; r2++) zero2[toRemove[r2]] = 0;
          try { var ru2 = await updateItems(zero2); if (ru2 && ru2.ok) { changed = true; freshCart = await ru2.json().catch(function () { return freshCart; }); } else okAll = false; }
          catch (e) { okAll = false; warn('remove-batch', e); }
        }
      }

      if (changed) {
        // [A2.3] Reuse the full cart from the last change.js when we have one (tier-down / adjust /
        // remove). A pure-add pass (fresh tier from no gifts) has only add.js responses = no full
        // cart, so fetch it once. Either way the drawer renders from a COMPLETE cart, never a partial.
        if (!freshCart || freshCart.total_price == null || !Array.isArray(freshCart.items)) {
          freshCart = await getCart();
        }
        var evtInit = freshCart ? { detail: { cart: freshCart } } : undefined;
        document.body.dispatchEvent(new CustomEvent('cart:updated', evtInit));
        if (addFired) document.body.dispatchEvent(new CustomEvent('product-added-to-cart', evtInit));
      }
    } catch (e) { okAll = false; warn('reconcile', e); }
    finally { window.__gdCartProcessing = false; }

    // [BLOCKER-1] anti-storm: if we tried to converge and a mutation failed, don't spin. After
    // MAX_NOPROGRESS consecutive failed passes, back off — the fetch monkey-patch would otherwise
    // re-trigger us on our own failed POST, amplifying a throttle/OOS storm under sale load.
    if (attempted && !okAll) {
      noProgress++;
      if (noProgress >= MAX_NOPROGRESS) {
        backoffUntil = nowMs() + BACKOFF_MS;
        noProgress = 0;
        warn('backoff', { message: 'cart mutation failing; pausing ' + BACKOFF_MS + 'ms' });
      }
    } else {
      noProgress = 0;
    }
    return changed;
  }

  function giftsMatchDesired(cart) {
    var desired = desiredGifts(cart);
    var tierTag = 'tier-' + (desired.__tier || 0);
    var caption = desired.__caption || '';
    var actual = new Map();
    (cart.items || []).forEach(function (it) {
      if (isTierGift(it)) {
        var p = it.properties || {};
        actual.set(Number(it.variant_id), { qty: Number(it.quantity) || 0, tag: p[GIFT_PROP], caption: p[CAPTION_PROP] || '' });
      }
    });
    if (actual.size !== desired.size) return false;
    var ok = true;
    desired.forEach(function (qty, vid) {
      var a = actual.get(vid);
      if (!a || a.qty !== qty || a.tag !== tierTag || a.caption !== caption) ok = false;  // [MEDIUM-8]
    });
    return ok;
  }

  // Checkout: best-effort converge, tightly bounded. This is ONLY ever run behind the hard
  // navigate-cap in the click handler below — it can take as long as it likes and the shopper
  // still leaves for /checkout on time. The mutex ceiling is 1600ms/pass so a SINGLE pass can fully
  // absorb an in-flight event reconcile (getCart + add + batched-remove ≈ 1.4s under live latency)
  // and then confirm — rather than burning a pass on a 700ms timeout while the peer is still
  // mid-removal (that was letting unearned gifts survive a drop→instant-checkout race). A
  // failing/slow gift ADD is still attempted at most `passes` times and logged — never retry-looped.
  // Returns true only when the cart already matches desired.
  async function reconcileUntilStable(maxPasses) {
    var passes = maxPasses || 2;
    for (var i = 0; i < passes; i++) {
      var waited = 0;
      while (window.__gdCartProcessing && waited < 1600) { await sleep(80); waited += 80; }
      var mutated = await reconcile(true);   // force: checkout attempts even during backoff
      await sleep(80);
      if (!mutated) {
        var cart = await getCart();
        if (cart && giftsMatchDesired(cart)) return true;   // converged → done
      }
      // else: no change but still mismatched (mutex was contended, or a gift ADD failed) → retry
      // once more (bounded to `passes`). REMOVE-FIRST on the checkout path already zeroed every
      // unearned gift in one batched update.js call; this loop is best-effort ADD convergence, and
      // the hard navigate-cap bounds total time regardless.
    }
    return false;
  }

  var CHECKOUT_SEL = 'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"], a[href$="/cart/checkout"], [data-gd-cart-checkout], [data-gd-checkout]';
  // [EMERGENCY HARDENING — Joel 2026-07-19, 2am] Checkout must NEVER block on gift convergence.
  // Root cause of the live hang: Tier-0 means EVERY cart has a Mystery Gift to add at checkout; a
  // failing/slow add made reconcileUntilStable burn its full multi-pass timeout before navigating.
  // Structural fix: a hard navigate-cap. We ALWAYS leave for /checkout within CHECKOUT_HARD_CAP_MS
  // no matter what the reconcile does (hang, reject, throw, slow network). Checkout beats gift
  // completeness — the server-side Shopify Flow net (Flow B) reconciles any gift we couldn't add
  // in time. There is no code path here that can trap the shopper.
  // Cap sized for LIVE latency (~450ms/cart round-trip). Under a drop→instant-checkout race the
  // checkout must (a) wait out an in-flight event reconcile that is removing the now-unearned gifts,
  // then (b) confirm — which measured ~1.8s live. 1500ms cut that off mid-removal and navigated with
  // UNEARNED gifts still in the cart (company cost). 2500ms lets the mutex-serialized removal finish
  // in that rare race while still guaranteeing no hang; the common (already-converged) checkout is
  // unaffected and still leaves in well under a second. Unearned-never-stranded > a marginally
  // longer worst-case checkout (Joel 2026-07-19).
  var CHECKOUT_HARD_CAP_MS = 2500;
  var checkoutBusy = false;
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest(CHECKOUT_SEL);
    if (!btn) return;
    // FULLY exception-wrapped: ANY throw from here on still fails open to /checkout — the shopper is
    // never trapped, even if preventDefault/guard/setup throws before the cart work begins.
    try {
      // preventDefault FIRST, then the busy-guard — a rage-click can't fall through to native nav.
      e.preventDefault();
      e.stopPropagation();
      if (checkoutBusy) return;   // rapid multi-clicks: first owns the flow, the rest are swallowed
      // Express/dynamic checkout buttons (Shop Pay / PayPal / Apple Pay) are Shopify iframes — not
      // matched here; the cart-event reconcile keeps their cart correct.
      checkoutBusy = true;
      // Arm the fail-open navigator + hard cap BEFORE any cart work, so the 2.5s timer is guaranteed
      // to fire even if everything below throws or hangs. (Nothing async precedes this point.)
      var navigated = false;
      function go() { if (navigated) return; navigated = true; window.location.href = '/checkout'; }
      var capTimer = setTimeout(go, CHECKOUT_HARD_CAP_MS);
      try {
        // Checkout now owns the cart: block background event reconciles (debounced() bails on this)
        // so the checkout reconcile is the SINGLE writer — no concurrent-mutation lost-update race.
        window.__gdCheckingOut = true;
        // Mutex-serialized reconcile: removes-first (batched update.js), then adds. The cap fires
        // independently, so even if the add pass is slow/stranded the shopper still leaves.
        Promise.resolve(reconcileUntilStable(2))
          .then(function (stable) { if (!stable) warn('checkout', { message: 'proceeding without confirmed gift convergence (Flow B net)' }); })
          .catch(function (err) { warn('checkout', err); })
          .finally(function () { clearTimeout(capTimer); go(); });
      } catch (err) {
        // Synchronous throw setting up the reconcile — cancel the cap + navigate immediately.
        warn('checkout', err);
        clearTimeout(capTimer);
        go();
      }
    } catch (outer) {
      // Last resort: even if preventDefault/guard/timer setup threw, leave for checkout — never dead-end.
      warn('checkout-setup', outer);
      try { window.location.href = '/checkout'; } catch (e2) {}
    }
  }, true);  // capture phase — beat theme/Dawn checkout handlers

  // Triggers
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { debounced(300); });
  } else { debounced(300); }
  // [A2.1 — Joel 2026-07-19] Burst debounce trimmed 140→110ms (still coalesces the add+event
  // storm, which all fire within a few ms). gd:cart-refreshed is NO LONGER a trigger: it is the
  // DRAWER's post-render echo, not a cart mutation, and every drawer refresh dispatched one —
  // which reset this debounce and pushed the gift reconcile ~1.2s late (see the tier-cross
  // profile). Real mutations are still fully covered by cart:updated + product-added-to-cart +
  // the /cart/(add|change|update).js fetch monkey-patch below, so convergence is unchanged.
  document.body.addEventListener('cart:updated', function () { debounced(110); });
  document.body.addEventListener('product-added-to-cart', function () { debounced(110); });

  // [A3 — Joel 2026-07-19] Read-only export for OPTIMISTIC drawer rendering. The drawer calls
  // these PURE helpers to know the target gift set for a cart and whether the cart already matches,
  // so it can paint shimmer gift rows instantly while this engine converges the real cart behind it.
  // These never mutate the cart and never touch the serialization/mutex — presentation only.
  window.gdTierGifts = {
    // → { count, caption, tier, variants:[vid…] } for the highest earned tier (empty when none).
    desired: function (cart) {
      var d = desiredGifts(cart);
      var vids = []; d.forEach(function (qty, vid) { vids.push(vid); });
      return { count: vids.length, caption: d.__caption || '', tier: d.__tier || 0, variants: vids };
    },
    // → true when the cart's _gd_gift lines already equal the desired set (nothing pending).
    matches: function (cart) { try { return giftsMatchDesired(cart); } catch (e) { return true; } }
  };

  // fetch monkey-patch — catch any /cart mutation from any code path (like the peer engines).
  var originalFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = originalFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)\.js/.test(url)) { result.then(function () { debounced(110); }).catch(function () {}); }
    return result;
  };
})();
